import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useToggle } from '@anytag/shared-hooks';
import { Prompt, TableV2 } from '@anytag/shared-atoms';
import { BudgetBulkEditModal } from '@anytag/shared-organisms';
import { ProposalJobDescriptionBreakDownMode } from '@anytag/graphql';
import { useUserProposalContext } from '../UserProposalContext';
import { SelectProposedInfluencersActionBar } from '../SelectProposedInfluencersActionBar';
import { ProposeInfluencersTableEmpty, ProposeInfluencersTableTitle } from '../TableComponents';
import { ProposeInfluencersFormValues, ProposeInfluencersTableMode, RowData } from '../schemaTypes';
import { ProposeInfluencersTableModes, ProposeInfluencersTableModesProps } from '../ProposeInfluencersModes';
import {
  useProposalBulkEdit,
  useInfluencersStateHandler,
  useProposedInfluencersTable,
  useSocialAccountsStateHandler,
  useProposedInfluencersSearch
} from '../hooks';

export interface TemplateProps {
  data: RowData[];
  loading: boolean;
  currency?: string;
  tableModeProps: Omit<ProposeInfluencersTableModesProps, 'onShowConfirmModal'>;
}

export const Template = ({ data, loading, currency, tableModeProps }: TemplateProps) => {
  const { t } = useTranslation();
  const { on: openBulkModal, toggle: toggleBulkModal } = useToggle();
  const { on: isConfirmModalOpen, setOff: closeConfirmModal, setOn: openConfirmModal } = useToggle();
  const { allSocialAccountIds, allSocialAccounts, selectedInfluencers } = useUserProposalContext();
  const {
    reset,
    formState: { isDirty }
  } = useFormContext<ProposeInfluencersFormValues>();
  const { isEditMode, isViewMode, setFilter } = useProposedInfluencersSearch();
  const {
    allInfluencerIds,
    selectedInfluencerAccountIds,
    setSelectedInfluencerAccountIds,
    handleSelectSingleInfluencer,
    handleToggleSelectAllInfluencers
  } = useInfluencersStateHandler(selectedInfluencers);
  const {
    selectedSocialAccounts,
    selectedSocialAccountIds,
    setSelectedSocialAccountIds,
    handleSelectSingleAccount,
    handleToggleSelectAllAccounts
  } = useSocialAccountsStateHandler({ allSocialAccounts, allSocialAccountIds });
  const { bulkLoading, handleInfluencersBudgetSubmit, handleSocialAccountBudgetSubmit } = useProposalBulkEdit({
    selectedSocialAccounts,
    selectedInfluencerAccountIds
  });

  useEffect(() => {
    if (isEditMode) {
      setSelectedSocialAccountIds([]);
    } else {
      setSelectedInfluencerAccountIds([]);
    }
  }, [isEditMode, selectedInfluencers.length]);

  const { jdMode } = tableModeProps;
  const isSocialMediaAccountsSelection = jdMode === ProposalJobDescriptionBreakDownMode.SOCIAL_BREAKDOWN && !isViewMode;
  const selectedAccountsIds = isSocialMediaAccountsSelection ? selectedSocialAccountIds : selectedInfluencerAccountIds;
  const selectedAccountsIdsLength = selectedAccountsIds.length;
  const hasAllSelectedIds = isSocialMediaAccountsSelection
    ? selectedAccountsIdsLength === allSocialAccountIds.length
    : selectedAccountsIdsLength === allInfluencerIds.length;

  const handleSelectAllIds = () => {
    if (isSocialMediaAccountsSelection) {
      setSelectedSocialAccountIds(allSocialAccountIds);
    } else {
      setSelectedInfluencerAccountIds(allInfluencerIds);
    }
  };

  const handleRowSelect = (influencerId: number, socialAccountId?: number) => {
    if (isSocialMediaAccountsSelection) {
      handleSelectSingleAccount(socialAccountId as number);
    } else {
      handleSelectSingleInfluencer(influencerId);
    }
  };

  const { columns } = useProposedInfluencersTable({
    jdMode,
    currency: currency || '',
    onRowSelect: handleRowSelect,
    selectButtonProps: { onClick: handleSelectAllIds },
    listSelectedIds: isSocialMediaAccountsSelection ? selectedSocialAccountIds : selectedInfluencerAccountIds
  });

  const handleToggleAllIds = () => {
    if (isSocialMediaAccountsSelection) {
      handleToggleSelectAllAccounts();
    } else {
      handleToggleSelectAllInfluencers();
    }
  };

  const handleBulkEditSuccess = () => {
    setFilter({ tableViewMode: ProposeInfluencersTableMode.VIEW });
    reset();
  };

  return (
    <>
      <ProposeInfluencersTableTitle title={t('Suggested Influencers')} />

      <ProposeInfluencersTableModes {...tableModeProps} onShowConfirmModal={openConfirmModal} />

      <div css={{ position: 'relative' }}>
        {selectedAccountsIdsLength > 0 ? (
          <SelectProposedInfluencersActionBar
            isViewMode={isViewMode}
            onSelectAll={handleSelectAllIds}
            hasAllSelected={hasAllSelectedIds}
            onToggleSelect={handleToggleAllIds}
            onToggleBulkEditModal={toggleBulkModal}
            numberSelected={selectedAccountsIdsLength}
            selectedSocialAccounts={selectedSocialAccounts}
            selectedInfluencerAccountIds={selectedInfluencerAccountIds}
            isSocialMediaAccountsSelection={isSocialMediaAccountsSelection}
          />
        ) : null}

        <TableV2
          noTopBorderRadius
          data={data}
          columns={columns}
          rowHeight="62px"
          className="proposal-table"
          empty={{
            hideTitle: true,
            description: <ProposeInfluencersTableEmpty />
          }}
          loading={loading}
          stickyHeader
          hasLoadingOverlay={loading}
        />
      </div>
      <BudgetBulkEditModal
        open={openBulkModal}
        onClose={toggleBulkModal}
        onSuccess={handleBulkEditSuccess}
        currency={currency || ''}
        jdMode={jdMode}
        loading={bulkLoading}
        onInfluencersBudgetSubmit={handleInfluencersBudgetSubmit}
        onSocialAccountBudgetSubmit={handleSocialAccountBudgetSubmit}
      />

      <Prompt
        size="lg"
        when={isDirty}
        open={isConfirmModalOpen}
        title={t('Dialog.Save your changes')}
        description={t('leaveWithoutSaveWarningText')}
        footer={({ reset, proceed }) => ({
          cancelButton: {
            title: t('Button.Leave without saving'),
            onClick: () => {
              setFilter({ tableViewMode: ProposeInfluencersTableMode.VIEW });
              closeConfirmModal();
              setTimeout(() => {
                proceed?.();
              }, 0);
            }
          },
          submitButton: {
            title: t('Button.Keep editing'),
            onClick: () => {
              closeConfirmModal();
              reset?.();
            }
          }
        })}
      />
    </>
  );
};
